import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useBusStationActions,
  useDropdownItemsCtxProvider,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  DrawerType,
  errors as errorsKeys,
  IRydeProcess,
  Log,
  RydeProcessTitle,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  useDrawers,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import {
  IImportBusStationsForm,
  importBusStationsValidationSchema,
} from "./import-bus-stations.models";

export const useImportBusStationsDrawer = () => {
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    importBusStationsValidationSchema,
    "busStation",
  );

  const { closeDrawer, openDrawer } = useDrawers();
  const { importStationsFromBusStations } = useBusStationActions();
  useDropdownItemsCtxProvider();

  const methods = useForm<IImportBusStationsForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: { busStationsIds: [], isCreateMissingAreas: false },
    resolver: resolver,
  });

  const {
    getValues,
    formState: { errors, isValid, touchedFields },
  } = methods;

  const isSaveDisabled = Boolean(
    !isValid ||
    Object.keys(errors || {}).length ||
    !touchedFields.busStationsIds,
  );

  const onSaveClick = useEvent(async (): Promise<SimpleResult<void>> => {
    if (!isValid) {
      return createErrorResult(errorsKeys.common.validationError);
    }

    const values = getValues();

    try {
      const bulkResult = await importStationsFromBusStations({
        busStationsIds: values.busStationsIds,
        isCreateMissingAreas: values.isCreateMissingAreas,
      });

      if (!bulkResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!bulkResult?.success) {
        return castErrorResult(bulkResult);
      }

      closeDrawer(DrawerType.ImportBusStations);

      const resultData = bulkResult?.data as IRydeProcess;

      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.BulkImport,
          entity: "station",
        },
      });
    } catch (error) {
      Log.error("Error during save bus stations import", error);
      warnAndToastSimpleResult(
        createErrorResult(
          errorsKeys.common.technicalProblemOccurred,
          "Error while run searchLocations method",
        ),
        t,
      );
    }

    return createSuccessResult();
  });

  return {
    methods,
    errors,
    isSaveDisabled,
    onSaveClick,
  };
};
